import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, ArrowLeft, CheckCircle, Upload, X, Image as ImageIcon, ArrowRight, Shield, Zap, FileImage, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import apiClient from '../lib/api';
import { getSessionId } from '../lib/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get or create session ID
const ConvertImagePage = () => {
  const { toast } = useToast();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [fromFormat, setFromFormat] = useState('auto');
  const [toFormat, setToFormat] = useState('jpg');
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('convert-image-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  const supportedFormats = [
    { value: 'jpg', label: 'JPG/JPEG' },
    { value: 'png', label: 'PNG' },
    { value: 'webp', label: 'WebP' },
    { value: 'bmp', label: 'BMP' },
    { value: 'gif', label: 'GIF' },
    { value: 'tiff', label: 'TIFF' }
  ];

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const detectImageFormat = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'jpg' || ext === 'jpeg') return 'jpg';
    if (ext === 'png') return 'png';
    if (ext === 'webp') return 'webp';
    if (ext === 'bmp') return 'bmp';
    if (ext === 'gif') return 'gif';
    if (ext === 'tiff' || ext === 'tif') return 'tiff';
    return 'unknown';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/gif', 'image/tiff'];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|bmp|gif|tiff|tif)$/i)) {
      toast({
        title: "Error",
        description: "Please upload a valid image file (JPG, PNG, WebP, BMP, GIF, or TIFF)",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size too large. Maximum 10MB allowed",
        variant: "destructive",
      });
      return;
    }

    setUploadingFile(true);
    
    try {
      const detectedFormat = detectImageFormat(file.name);
      
      const imageData = {
        file: file,
        name: file.name,
        size: formatFileSize(file.size),
        format: detectedFormat,
        uploadDate: new Date().toISOString().split('T')[0],
        preview: URL.createObjectURL(file)
      };

      setUploadedImage(imageData);
      setFromFormat(detectedFormat);
      setConvertResult(null);
      
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveImage = () => {
    if (uploadedImage?.preview) {
      URL.revokeObjectURL(uploadedImage.preview);
    }
    setUploadedImage(null);
    setConvertResult(null);
  };

  const handleConvert = async () => {
    if (!uploadedImage) {
      toast({
        title: "Error",
        description: "Please upload an image to convert",
        variant: "destructive",
      });
      return;
    }

    if (fromFormat === toFormat) {
      toast({
        title: "Error",
        description: "Source and target formats are the same",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedImage.file);
      formData.append('to_format', toFormat);

      const response = await apiClient.post(
        `${BACKEND_URL}/api/image/convert?session_id=${sessionId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setConvertResult(response.data);
      
      toast({
        title: "Success",
        description: `Image converted to ${toFormat.toUpperCase()} successfully`,
      });
    } catch (error) {
      console.error('Convert error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to convert image",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!convertResult) return;
    
    try {
      const response = await apiClient.get(
        `${BACKEND_URL}${convertResult.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', convertResult.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${convertResult.name}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('convert-image-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Convert Image Format Online — JPG, PNG, WebP, HEIC | AllSmallTooLS</title>
        <meta name="description" content="Convert your images between JPG, PNG, WebP, and more. Fast, free, and secure online image converter." />
        <meta name="keywords" content="convert image, jpg to png, png to jpg, webp converter, free image converter" />
        <link rel="canonical" href="https://allsmalltools.com/convert-image" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to image converter anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your image converter for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <ImageIcon className="h-8 w-8 text-blue-500" />
              <span>Convert Image</span>
            </h1>
            <p className="text-gray-600">Convert images between different formats</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload & Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Upload & Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Image Upload */}
            {!uploadedImage ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  uploadingFile ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={uploadingFile ? undefined : handleDrag}
                onDragLeave={uploadingFile ? undefined : handleDrag}
                onDragOver={uploadingFile ? undefined : handleDrag}
                onDrop={uploadingFile ? undefined : handleDrop}
                onClick={uploadingFile ? undefined : () => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={uploadingFile}
                />
                
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                  dragActive ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Upload className={`h-8 w-8 ${dragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {uploadingFile ? 'Loading Image...' : 'Upload Image'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {uploadingFile ? 'Please wait...' : 'Drag and drop your image here, or click to browse'}
                </p>
                <p className="text-sm text-gray-500">
                  Supported formats: JPG, PNG, WebP, BMP, GIF, TIFF • Max 10MB
                </p>
              </div>
            ) : (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <img 
                      src={uploadedImage.preview} 
                      alt="Preview" 
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{uploadedImage.name}</p>
                      <p className="text-sm text-gray-500">
                        {uploadedImage.format.toUpperCase()} • {uploadedImage.size}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveImage}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Format Selection */}
            {uploadedImage && (
              <div className="space-y-4">
                <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                  <div className="space-y-2">
                    <Label>From Format</Label>
                    <Select value={fromFormat} onValueChange={setFromFormat}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {supportedFormats.map(format => (
                          <SelectItem key={format.value} value={format.value}>
                            {format.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <ArrowRight className="h-6 w-6 text-gray-400 mt-6" />
                  
                  <div className="space-y-2">
                    <Label>To Format</Label>
                    <Select value={toFormat} onValueChange={setToFormat}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {supportedFormats.map(format => (
                          <SelectItem key={format.value} value={format.value}>
                            {format.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleConvert}
                  disabled={loading || fromFormat === toFormat}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                >
                  {loading ? 'Converting...' : 'Convert Image'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Conversion Result</CardTitle>
          </CardHeader>
          <CardContent>
            {convertResult ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Conversion Complete!</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Your image has been converted to {toFormat.toUpperCase()}
                  </p>
                </div>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{convertResult.name}</p>
                        <p className="text-sm text-gray-500">{convertResult.size}</p>
                      </div>
                      <Button
                        onClick={handleDownload}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No conversion yet</p>
                <p className="text-sm">Upload an image and select formats to convert</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Convert Image Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Image Converter?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileImage className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">All Formats Supported</h3>
              <p className="text-gray-600">
                Convert between JPG, PNG, WebP, GIF, BMP, TIFF, and more. Support for all major image formats makes format conversion simple and flexible.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quality Preserved</h3>
              <p className="text-gray-600">
                Our image converter maintains high quality during format conversion. Original image quality is preserved while changing file formats.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Instant Conversion</h3>
              <p className="text-gray-600">
                Convert images in seconds with our fast online tool. No software installation or registration needed – just upload and convert.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Whether you need to convert JPG to PNG, PNG to WebP, or any other image format – our free image converter makes it easy. Perfect for web optimization, compatibility requirements, or preparing images for different platforms. Try our convert image tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/resize-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Resize Images
              </Link>
              <Link to="/compress-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Compress Images
              </Link>
              <Link to="/remove-background" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Remove Background
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Convert Images Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Convert Image Formats Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload Your Image:</strong> Click the upload area or drag and drop your image file. Our converter supports all major image formats.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Select Output Format:</strong> Choose your desired output format from the dropdown – JPG, PNG, WebP, GIF, BMP, TIFF, or ICO.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Convert Image:</strong> Click "Convert Image" button. Our tool will process your image and convert it to the selected format in seconds.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Converted Image:</strong> Download your image in the new format. Preview the result before downloading.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is image conversion free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our image converter is completely free. Convert unlimited images between formats without registration, fees, or watermarks.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">What image formats are supported?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">We support all major formats: JPG/JPEG, PNG, WebP, GIF, BMP, TIFF, and ICO. Convert between any of these formats easily.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Will image quality be lost?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Quality depends on the target format. Converting to lossless formats like PNG maintains quality. Converting to JPG may slightly compress the image. We optimize for the best quality in each format.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is it safe to convert images online?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! All images are processed securely. We automatically delete uploaded and converted files after processing. Your images remain private.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default ConvertImagePage;
